<template>
  <div ref="heroWrapper" class="hero-wrapper">
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>

    <div class="hero-content">
      <span class="badge-pill" :class="{ visible: titleVisible }">
        AI-Powered · Vector Search · Smart Writing
      </span>

      <h1 class="hero-title gradient-b" :class="{ visible: titleVisible }">
        <span class="title-line title-line-top">
          <span class="where-light">Where</span> <span class="accent-text accent-top convo-dark">conversations</span>
        </span><br />
        <span class="title-line title-line-bottom">
          <span class="italic-line"><span class="become-dark">become</span> <span class="accent-text accent-bottom">conversions</span><span class="dot-dark">.</span></span>
        </span>
      </h1>

      <p class="hero-subtitle" :class="{ visible: titleVisible }">
        Chat · Outreach · Writing — powered by Vectorflux
      </p>

      <div class="hero-actions" :class="{ visible: titleVisible }">
        <router-link v-if="isLoggedIn" to="/chats" class="hero-btn btn-primary-solid">
          {{ $t("HomeView.enter_workspace") }}
        </router-link>
        <router-link v-else to="/login" class="hero-btn btn-primary-solid">
          {{ $t("HomeView.login_now") }}
        </router-link>
      </div>
      <p v-if="!isLoggedIn" class="hero-hint" :class="{ visible: titleVisible }">
        {{ $t("HomeView.login_hint") }}
      </p>

      <div class="feature-pills" :class="{ visible: titleVisible }">
        <span class="feature-pill">{{ $t("HomeView.feature_vector_search") }}</span>
        <span class="feature-pill">{{ $t("HomeView.feature_ai_chat") }}</span>
        <span class="feature-pill">{{ $t("HomeView.feature_smart_writing") }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";

const REPULSION_DISTANCE = 110;
const MAX_SPEED = 1.8;
const PARTICLE_COLORS = [
  "142,115,248", // #8e73f8
  "79,127,240",  // #4f7ff0
  "26,148,230",  // #1a94e6
  "18,120,207",  // #1278cf
];

export default defineComponent({
  name: "HomeView",
  data() {
    return {
      titleVisible: false,
      particles: [],
      ctx: null,
      rafId: null,
      mouseX: -9999,
      mouseY: -9999,
      mouseActive: false,
      enableRepulsion: false,
      hasTriggeredTitle: false,
      repulsionDelayTimer: null,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isAuthenticated;
    },
  },
  mounted() {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      this.titleVisible = true;
      this.hasTriggeredTitle = true;
      return;
    }

    this.initParticles();
    window.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("mouseleave", this.handleMouseLeave);
    window.addEventListener("resize", this.handleResize);
    // Auto start hero reveal quickly even without mouse movement.
    setTimeout(() => {
      if (!this.hasTriggeredTitle) {
        this.hasTriggeredTitle = true;
        this.titleVisible = true;
      }
    }, 900);
    this.repulsionDelayTimer = setTimeout(() => {
      this.enableRepulsion = true;
    }, 1000);
    this.animate();
  },
  beforeUnmount() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.repulsionDelayTimer) {
      clearTimeout(this.repulsionDelayTimer);
    }
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseleave", this.handleMouseLeave);
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    getParticleCount() {
      const width = window.innerWidth || 1280;
      if (width < 768) return 80;
      if (width < 1200) return 110;
      return 150;
    },
    initParticles() {
      const canvas = this.$refs.particleCanvas;
      if (!canvas) return;

      this.ctx = canvas.getContext("2d");
      this.resizeCanvas();
      this.particles = [];

      const particleCount = this.getParticleCount();
      for (let i = 0; i < particleCount; i += 1) {
        const radius = 1.8 + Math.random() * 1.4;
        const colorRgb = PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)];
        // Smaller particles move faster, larger ones move slower for depth feel.
        const speedFactor = Math.max(0.4, Math.min(1, (3.4 - radius) / 1.6));
        const baseVx = (Math.random() - 0.5) * 0.7 * speedFactor;
        const baseVy = (Math.random() - 0.5) * 0.7 * speedFactor;
        this.particles.push({
          x: Math.random() * canvas.clientWidth,
          y: Math.random() * canvas.clientHeight,
          vx: baseVx,
          vy: baseVy,
          baseVx,
          baseVy,
          radius,
          color: `rgba(${colorRgb}, ${0.18 + Math.random() * 0.24})`,
        });
      }
    },
    resizeCanvas() {
      const canvas = this.$refs.particleCanvas;
      const wrapper = this.$refs.heroWrapper;
      if (!canvas || !wrapper) return;

      const dpr = window.devicePixelRatio || 1;
      const width = wrapper.clientWidth;
      const height = wrapper.clientHeight;

      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      if (this.ctx) {
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    },
    handleResize() {
      this.resizeCanvas();
      this.initParticles();
    },
    handleMouseMove(event) {
      const canvas = this.$refs.particleCanvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      this.mouseX = event.clientX - rect.left;
      this.mouseY = event.clientY - rect.top;
      this.mouseActive = true;

      if (!this.hasTriggeredTitle) {
        this.hasTriggeredTitle = true;
        this.titleVisible = true;
      }
    },
    handleMouseLeave() {
      this.mouseActive = false;
      this.mouseX = -9999;
      this.mouseY = -9999;
    },
    animate() {
      if (!this.ctx) {
        this.rafId = requestAnimationFrame(this.animate);
        return;
      }

      const canvas = this.$refs.particleCanvas;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      this.ctx.clearRect(0, 0, width, height);

      this.particles.forEach((particle) => {
        if (this.mouseActive && this.enableRepulsion) {
          const dx = particle.x - this.mouseX;
          const dy = particle.y - this.mouseY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist > 0 && dist < REPULSION_DISTANCE) {
            const strength = ((REPULSION_DISTANCE - dist) / REPULSION_DISTANCE) * 0.6;
            particle.vx += (dx / dist) * strength;
            particle.vy += (dy / dist) * strength;
          }
        }

        particle.vx += (particle.baseVx - particle.vx) * 0.02;
        particle.vy += (particle.baseVy - particle.vy) * 0.02;
        particle.vx = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, particle.vx));
        particle.vy = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, particle.vy));

        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0) {
          particle.x = 0;
          particle.vx *= -0.6;
        } else if (particle.x > width) {
          particle.x = width;
          particle.vx *= -0.6;
        }

        if (particle.y < 0) {
          particle.y = 0;
          particle.vy *= -0.6;
        } else if (particle.y > height) {
          particle.y = height;
          particle.vy *= -0.6;
        }

        this.ctx.beginPath();
        this.ctx.fillStyle = particle.color;
        this.ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        this.ctx.fill();
      });

      this.rafId = requestAnimationFrame(this.animate);
    },
  },
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500&family=Manrope:wght@500;700&family=Noto+Sans+JP:wght@400;500;700&family=Noto+Sans+TC:wght@400;500;700&display=swap");

.hero-wrapper {
  position: relative;
  width: 100vw;
  min-height: 100vh;
  margin-top: -4.5rem;
  padding-top: 4.5rem;
  margin-left: calc(50% - 50vw);
  background: #ffffff;
  overflow: hidden;
}

.hero-wrapper::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease;
}

.particle-canvas {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
  min-height: calc(100vh - 4.5rem);
  padding: 0 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transform: translateY(-4vh);
  font-family: "Inter", "Noto Sans JP", "Noto Sans TC", "Hiragino Kaku Gothic ProN",
    "Yu Gothic", "PingFang TC", "Microsoft JhengHei", "Segoe UI",
    "Helvetica Neue", Arial, sans-serif;
  text-align: center;
}

.badge-pill {
  display: inline-block;
  border: 1px solid #e5e7eb;
  color: #6b7280;
  background: #ffffff;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 1.1s cubic-bezier(0.22, 1, 0.36, 1) 0.2s,
    transform 1.1s cubic-bezier(0.22, 1, 0.36, 1) 0.2s;
}

.hero-title {
  margin: 1.4rem 0 1rem;
  font-family: "Manrope", "Inter", "Noto Sans JP", "Noto Sans TC",
    "Hiragino Kaku Gothic ProN", "Yu Gothic", "PingFang TC",
    "Microsoft JhengHei", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.01em;
  display: inline-block;
  text-align: left;
  overflow: visible;
  color: #111827;
  text-shadow: 0 0 1px rgba(0, 0, 0, 0.06);
  opacity: 0;
  transform: scale(0.98);
  transition: opacity 1.5s cubic-bezier(0.22, 1, 0.36, 1),
    transform 1.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-title.gradient-a .where-light,
.hero-title.gradient-a .accent-bottom {
  background-image: linear-gradient(135deg, #7d4dff 0%, #6358ff 38%, #2f6bff 72%, #00a8ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.hero-title.gradient-b .where-light,
.hero-title.gradient-b .accent-bottom {
  background-image: linear-gradient(135deg, #8e73f8 0%, #4f7ff0 44%, #1a94e6 76%, #1278cf 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.italic-line {
  font-style: italic;
  display: inline-block;
  line-height: 1.2;
  padding-right: 0.14em;
  margin-right: -0.14em;
}

.title-line {
  display: inline-block;
}

.title-line-top {
  transform: translateX(-0.65rem);
}

.title-line-bottom {
  transform: translateX(0.55rem);
}

.hero-subtitle {
  margin: 0 auto;
  max-width: 680px;
  color: #6b7280;
  font-size: 1.05rem;
  line-height: 1.9;
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1) 0.34s,
    transform 1s cubic-bezier(0.22, 1, 0.36, 1) 0.34s;
}

.hero-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1) 0.5s,
    transform 1s cubic-bezier(0.22, 1, 0.36, 1) 0.5s;
}

.hero-hint {
  margin-top: 0.65rem;
  margin-bottom: 0;
  color: #6b7280;
  font-size: 0.78rem;
  letter-spacing: 0.01em;
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.62s,
    transform 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.62s;
}

.hero-btn {
  min-width: 150px;
  text-decoration: none;
  border-radius: 10px;
  font-weight: 500;
  padding: 0.7rem 1.2rem;
  transition: all 0.2s ease;
}

.btn-primary-solid {
  background: #2b66ff;
  color: #ffffff;
  border: 1.5px solid #2b66ff;
  box-shadow: 0 4px 10px rgba(43, 102, 255, 0.22);
}

.btn-primary-solid:hover {
  background: #1f54dd;
  border-color: #1f54dd;
  color: #ffffff;
  transform: translateY(-0.5px);
  box-shadow: 0 6px 14px rgba(43, 102, 255, 0.24);
}

.btn-primary-solid:focus-visible {
  outline: 3px solid rgba(43, 102, 255, 0.35);
  outline-offset: 2px;
}

.feature-pills {
  margin-top: 1.6rem;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1) 0.74s,
    transform 1s cubic-bezier(0.22, 1, 0.36, 1) 0.74s;
}

.feature-pill {
  background: #f3f4f6;
  color: #111827;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  font-size: 0.82rem;
}

.accent-text {
  display: inline-block;
  padding-right: 0.03em;
  color: inherit;
  text-shadow: none;
  opacity: 1;
}

.where-light {
  text-shadow: 0 0 1px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}

.convo-dark {
  font-size: 0.9em;
  color: #000000;
  -webkit-text-fill-color: #000000;
  background: none;
  -webkit-background-clip: border-box;
  background-clip: border-box;
}

.become-dark {
  font-size: 0.9em;
  color: #000000;
  -webkit-text-fill-color: #000000;
  background: none;
  -webkit-background-clip: border-box;
  background-clip: border-box;
}

.dot-dark {
  color: #000000;
  -webkit-text-fill-color: #000000;
  background: none;
  -webkit-background-clip: border-box;
  background-clip: border-box;
}

.accent-top {
  animation: none;
}

.accent-bottom {
  animation: none;
  padding-right: 0.1em;
  margin-right: -0.08em;
  font-weight: 700;
}

@keyframes accent-flow-ltr {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes accent-flow-rtl {
  0% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
}

.hero-title.visible,
.hero-subtitle.visible,
.hero-actions.visible,
.feature-pills.visible,
.badge-pill.visible,
.hero-hint.visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@media (max-width: 768px) {
  .hero-wrapper {
    min-height: 100vh;
  }

  .hero-content {
    min-height: calc(100vh - 4.5rem);
    padding: 0 1rem 1.5rem;
    transform: translateY(-2vh);
  }

  .hero-subtitle {
    font-size: 0.95rem;
    line-height: 1.75;
  }

  .title-line-top {
    transform: translateX(-0.35rem);
  }

  .title-line-bottom {
    transform: translateX(0.25rem);
  }

  .hero-btn {
    width: 100%;
    max-width: 320px;
  }
}

@media (min-width: 1024px) {
  .hero-wrapper::before {
    /* Desktop background image with light overlay for text readability. */
    background:
      linear-gradient(
        180deg,
        rgba(247, 250, 255, 0.68) 0%,
        rgba(247, 250, 255, 0.68) 100%
      ),
      url("../pexels-gdtography-277628-911738.jpg")
        center top / cover no-repeat;
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-title,
  .hero-subtitle,
  .hero-actions,
  .feature-pills,
  .badge-pill,
  .hero-hint {
    transition: none !important;
    animation: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
}
</style>
